import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { produce } from 'immer';
import moment from 'moment';
import { useApiFetch } from './api/useApiFetch';
import { IListResponse } from '../types/api/responses';
import { IListFilter } from '../types/api/requests';
import PusherService from '../services/pusher';
import { IAvitoChatsLayout } from '../types/layouts/avito-chats';

export const useAvitoChats = ({ channel }: IAvitoChatsLayout) => {
    const api = useApiFetch();
    const [chats, setChats] = useState<IListResponse|null>(null);
    const [filters, setFilters] = useState<IListFilter[]>([]);
    const [loading, setLoading] = useState(true);
    const [unread, setUnread] = useState(0);
    const [updateList, setUpdateList] = useState(false);
    const [searchParams] = useSearchParams();
    const page = searchParams.get('page') || 1;
    const pageSize = searchParams.get('pageSize') || 10;
    const search = searchParams.get('search');

    const pusher = useRef(new PusherService());

    const addTodayFilter = () => {
        setFilters(produce(filters, (draft) => {
            draft.push({
                column: 'last_message_created_at',
                values: [moment().format('YYYY-MM-DD')],
                operator: '>=',
            });
            draft.push({
                column: 'last_message_read_at',
                values: [null as any],
            });
            draft.push({
                column: 'last_message_direction',
                values: ['in'],
            });
        }));
    };

    const getUnread = async () => {
        const response = await api.fetchData<{ unread: number }>('/avito-chats/unread', 'get', true);
        if (response.success && response.data) {
            setUnread(response.data.unread);
        }
    };

    const getChats = async (showLoader = true) => {
        api.cancelRequest();

        if (showLoader) {
            setLoading(true);
        }

        const response = await api.fetchData<IListResponse>(`/avito-chats/list?page=${page}`, 'post', true, {
            count: pageSize,
            filters,
            search,
        });

        getUnread();

        setChats(response.data);
        setUpdateList(false);
        setLoading(false);
    };

    const onFiltersChange = (column: string, values: any[]) => {
        setFilters(produce(filters, (draft) => {
            const index = draft.findIndex((el) => el.column === column);
            if (values.length > 0) {
                if (index > -1) {
                    draft[index].values = values;
                } else {
                    draft.push({ column, values });
                }
            } else if (index > -1) {
                draft.splice(index, 1);
            }
        }));
    };

    const openSocket = () => {
        pusher.current.open(channel);
        pusher.current.registerMessageListener('new-message', () => setUpdateList(true));
    };

    useEffect(() => {
        getChats();
    }, [filters, page, pageSize, search]);

    useEffect(() => {
        if (updateList) {
            getChats(false);
        }
    }, [updateList]);

    useEffect(() => {
        openSocket();
        return () => {
            pusher.current.close();
        };
    }, []);

    return {
        addTodayFilter,
        chats,
        clearFilters: () => setFilters([]),
        filters,
        loading,
        onFiltersChange,
        unread,
    };
};
